import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { formatDateShort, classifyDue } from "@/lib/format";
import { useEffortUnit, formatEffort } from "@/lib/effort";

function MilestonesView() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [dueDate, setDueDate] = useState("");

  const q = useQuery({
    queryKey: queryKeys.milestonesFor(projectId),
    queryFn: () => api.listMilestones(projectId),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.milestonesFor(projectId) });

  const create = useMutation({
    mutationFn: () =>
      api.createMilestone(projectId, {
        name: name.trim(),
        dueDate: dueDate || undefined,
      }),
    onSuccess: () => {
      setName("");
      setDueDate("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteMilestone(id),
    onSuccess: () => {
      invalidate();
      // A deleted milestone clears milestone_id on its tasks.
      qc.invalidateQueries({ queryKey: queryKeys.tasksFor(projectId) });
    },
  });

  const effortUnit = useEffortUnit();
  const milestones = q.data?.milestones ?? [];

  return (
    <div className="proj-shell-content">
      <form
        className="ms-create"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          className="input"
          placeholder="New milestone name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Milestone name"
        />
        <input
          className="input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          aria-label="Milestone due date"
          style={{ width: 170 }}
        />
        <Button
          variant="primary"
          type="submit"
          disabled={!name.trim() || create.isPending}
        >
          {create.isPending ? "Adding…" : "Add milestone"}
        </Button>
      </form>

      {q.isLoading ? (
        <Skeleton count={3} height={70} />
      ) : milestones.length === 0 ? (
        <EmptyState
          icon="flag"
          title="No milestones yet."
          caption="Create a milestone above, then assign tasks to it from the task detail."
        />
      ) : (
        <ul className="ms-list">
          {milestones.map((m) => {
            const pct =
              m.taskCount > 0
                ? Math.round((m.doneCount / m.taskCount) * 100)
                : 0;
            const overdue = m.dueDate && classifyDue(m.dueDate) === "overdue";
            return (
              <li key={m.id} className="ms-card">
                <div className="ms-head">
                  <span className="ms-name">{m.name}</span>
                  {m.dueDate && (
                    <span className={`ms-due${overdue ? " over" : ""}`}>
                      <Icon name="cal" size={12} /> {formatDateShort(m.dueDate)}
                    </span>
                  )}
                  <button
                    type="button"
                    className="icon-btn ms-del"
                    aria-label={`Delete milestone ${m.name}`}
                    onClick={() => remove.mutate(m.id)}
                  >
                    ✕
                  </button>
                </div>
                <div className="ms-prog-lab">
                  <span>
                    {m.doneCount}/{m.taskCount} tasks done
                    {m.estimateSum > 0 &&
                      ` · ${formatEffort(m.estimateSum, effortUnit)}`}
                  </span>
                  <span>{pct}%</span>
                </div>
                <div
                  className="progress ms-prog"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="bar" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/milestones")({
  component: MilestonesView,
});
